
import EventPublisher from "../utils/EventPublisher";
import PublicRequestExecuter from "./requestExecuters/PublicRequestExecuter";
import PublicResponseHandler from "./ResponseHandlers/PublicResponseHandler";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import SecureResponseHandler from "./ResponseHandlers/SecureResponseHandler";
import SecureRequestExecuter from "./requestExecuters/SecureRequestExecuter";


export class Space {

    constructor(
        spaceId,
        title,
        description,
        pricePerDay,
        minimumDays,
        statusId,
        status,
        memberId,
        addressId,
        googlePlaceId,
        latitude,
        longitude,
        formattedAddress,
        distance,
        created,
        updated,
        deleted,
        imageNames
    ) {
        this.spaceId = spaceId;
        this.title = title;
        this.description = description;
        this.pricePerDay = pricePerDay;
        this.minimumDays = minimumDays;
        this.statusId = statusId;
        this.status = status;
        this.memberId = memberId;
        this.addressId = addressId;
        this.googlePlaceId = googlePlaceId;
        this.latitude = latitude;
        this.longitude = longitude;
        this.formattedAddress = formattedAddress;
        this.distance = distance;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
        this.imageNames = imageNames;
    }

    equals(other) {
        return this.spaceId === other.spaceId &&
        this.title === other.title &&
        this.description === other.description &&
        this.pricePerDay === other.pricePerDay &&
        this.minimumDays === other.minimumDays &&
        this.statusId === other.statusId &&
        this.status === other.status &&
        this.memberId === other.memberId &&
        this.addressId === other.addressId &&
        this.googlePlaceId === other.googlePlaceId &&
        this.latitude === other.latitude &&
        this.longitude === other.longitude &&
        this.formattedAddress === other.formattedAddress &&
        this.distance === other.distance &&
        this.created === other.created &&
        this.updated === other.updated &&
        this.deleted === other.deleted &&
        this.imageNames === other.imageNames;
    }
}

class Reservation {
    constructor(
        reservationRequestId,
        advertisementId,
        advertisementTitle,
        spaceId,
        spaceTitle,
        statusId,
        statusName,
        startDate,
        endDate,
        created,
        updated,
        deleted,
        pricePerDay,
        hasOverLappingRequests
    ) {
        this.reservationRequestId = reservationRequestId;
        this.advertisementId = advertisementId;
        this.advertisementTitle = advertisementTitle;
        this.spaceId = spaceId;
        this.spaceTitle = spaceTitle;
        this.statusId = statusId;
        this.statusName = statusName;
        this.startDate = startDate;
        this.endDate = endDate;
        this.created = created;
        this.updated = updated;
        this.deleted = deleted;
        this.pricePerDay = pricePerDay;
        this.hasOverLappingRequests = hasOverLappingRequests;
    }

    equals(other) {
        return this.reservationRequestId === other.reservationRequestId &&
            this.advertisementId === other.advertisementId &&
            this.advertisementTitle === other.advertisementTitle &&
            this.spaceId === other.spaceId &&
            this.spaceTitle === other.spaceTitle &&
            this.statusId === other.statusId &&
            this.statusName === other.statusName &&
            this.startDate === other.startDate &&
            this.endDate === other.endDate &&
            this.created === other.created &&
            this.updated === other.updated &&
            this.deleted === other.deleted &&
            this.pricePerDay === other.pricePerDay &&
            this.hasOverLappingRequests === other.hasOverLappingRequests;
    }
}

class MemberSpacesReservationResponse {
    constructor(
        reservationRequests,
        activeReservations,
        history
    ) {
        this.reservationRequests = reservationRequests;
        this.activeReservations = activeReservations;
        this.history = history;
    }
}

class FindSpacesResponse {
    constructor(results, latitude, longitude, count) {
        this.results = results;
        this.latitude = latitude;
        this.longitude = longitude;
        this.count = count;
    }
}

class GetSpacesResponse {
    constructor(results) {
        this.results = results;
    }
}

class GetPrivateSpaceResponse {
    constructor(space, isLimitedEdit, googleAddress) {
        this.space = space;
        this.isLimitedEdit = isLimitedEdit;
        this.googleAddress = googleAddress;
    }
}

class GetMySpacesResponse {
    constructor(listedSpaces, unlistedSpaces) {
        this.listedSpaces = listedSpaces;
        this.unlistedSpaces = unlistedSpaces;
    }
}

class SpaceMonthEarningEvent {
    constructor(day, advertisementId, advertisementTitle, spaceId, spaceTitle, pricePerDay) {
        this.day = day;
        this.advertisementId = advertisementId;
        this.advertisementTitle = advertisementTitle;
        this.spaceId = spaceId;
        this.spaceTitle = spaceTitle;
        this.pricePerDay = pricePerDay;
    }
}

class SpaceMonthPayoutEvent {
    constructor(day, paymentIdentifier, amount) {
        this.day = day;
        this.paymentIdentifier = paymentIdentifier;
        this.amount = amount;
    }
}


class SpaceMonthBalanceEvents {
    constructor(priorEarnings, priorPayouts, earningsEvents, payoutEvents) {
        this.priorEarnings = priorEarnings;
        this.priorPayouts = priorPayouts;
        this.earningsEvents = earningsEvents;
        this.payoutEvents = payoutEvents;
    }
}


export default class SessionAPIClient {
    constructor() {
        this.findSpaceHandler =
            new PublicResponseHandler(
                new Map()
                    .set("SPACE_FOUND", this.onSearchSuccess)
                    .set("INVALID_SPACES_SEARCH_QUERY_PARAMS", this.onInvalidQuery)
                    .set("GOOGLE_API_DOWN", this.onGoogleAPIError)
                    .set("INVALID_GOOGLE_PLACE_ID", this.onGoogleAPIError));
        this.getSpaceHandler =
            new PublicResponseHandler(
                new Map()
                    .set("SPACE_DOES_NOT_EXIST", this.onSpaceNotFound)
                    .set("SPACE_FOUND", this.onSpaceFound));
        this.getPrivateSpaceDetailsHandler =
            new PublicResponseHandler(
                new Map()
                    .set("SPACE_DOES_NOT_EXIST", this.onPrivateSpaceDetailsNotFound)
                    .set("SPACE_FOUND", this.onPrivateSpaceDetailsFound));
        this.getPrivateSpaceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_DOES_NOT_EXIST", this.onPrivateSpaceNotFound)
                    .set("SPACE_FOUND", this.onPrivateSpaceFound));
        this.getListedSpacesHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_FOUND", this.onGetListedSpacesSuccess));
        this.getUnlistedSpacesHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_FOUND", this.onGetUnlistedSpacesSuccess));
        this.getMySpacesHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_FOUND", this.onGetMySpacesSuccess));
        this.createSpaceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_CREATED", this.onSpaceCreatedSuccess));
        this.updateSpaceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_UPDATED", this.onSpaceUpdateSuccess));
        this.unlistSpaceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_UNLISTED", this.onSpaceStatusChangeSuccess));
        this.listSpaceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_LISTED", this.onSpaceStatusChangeSuccess));
        this.getMemberSpacesReservationsHandler =
            new SecureResponseHandler(
                new Map()
                    .set("SPACE_RESERVATIONS_COLLECTED", this.onMemberSpacesReservationsSuccess));
        this.getMemberAdvertisementReservationsHandler =
            new SecureResponseHandler(
                new Map()
                    .set("ADVERTISEMENT_RESERVATIONS_COLLECTED", this.onMemberAdvertisementReservationsSuccess));
        this.rejectSpaceReservationHandler =
            new SecureResponseHandler(
                new Map()
                    .set("RESERVATION_REJECTED", this.onSpaceRejected));
        this.acceptSpaceReservationHandler =
            new SecureResponseHandler(
                new Map()
                    .set("RESERVATION_ACCEPTED", this.onSpaceAccepted));
        this.getSpaceBalanceHandler =
            new SecureResponseHandler(
                new Map()
                    .set("BALANCE_LOADED", this.onGetSpaceBalanceSuccess));
    }

    findSpaces(
        googlePlaceId,
        minPricePerDay,
        maxPricePerDay,
        maxDistance,
        minDistance,
        limit,
        offset,
        orderBy,
        order,
        latitude = 0,
        longitude = 0) {
        PublicRequestExecuter.executeGETRequest(
            "/api/find/spaces",
            {
                "google_place_id": googlePlaceId,
                "min_price_per_day": minPricePerDay,
                "max_price_per_day": maxPricePerDay,
                "max_distance": maxDistance,
                "min_distance": minDistance,
                "limit": limit,
                "offset": offset,
                "order_by": orderBy,
                "order": order,
                "latitude": latitude,
                "longitude": longitude,
            },
            {},
            this.findSpaceHandler);
    }

    onInvalidQuery = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.INVALID_SEARCH_QUERY, "INVALID_SEARCH_QUERY");
    };

    onGoogleAPIError = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.SERVER_ERROR, "SERVER_ERROR");
    };

    onSearchSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.FIND_SPACE_LOADED,
            new FindSpacesResponse(
                responseBodyContents["results"].map(space => new Space (
                    parseInt(space["space_id"], 10),
                    space["title"],
                    space["description"],
                    parseFloat(space["price_per_day"]),
                    parseInt(space["minimum_days"], 10),
                    parseInt(space["status_id"], 10),
                    space["status"],
                    parseInt(space["member_id"], 10),
                    parseInt(space["current_address_id"], 10),
                    space["current_google_place_id"],
                    space["current_latitude"],
                    space["current_longitude"],
                    space["current_formatted_address"],
                    space["distance"],
                    space["created"],
                    space["updated"],
                    space["deleted"],
                    space["image_names"]
                )),
                responseBodyContents["latitude"],
                responseBodyContents["longitude"],
                responseBodyContents["count"])
        );
    };

    getSpace(spaceId) {
        PublicRequestExecuter.executeGETRequest(
            "/api/space/" + spaceId,
            {},
            {},
            this.getSpaceHandler);
    }

    getPrivateSpaceDetails(spaceId) {
        PublicRequestExecuter.executeGETRequest(
            "/api/space/" + spaceId,
            {},
            {},
            this.getPrivateSpaceDetailsHandler);
    }

    getPrivateSpace(spaceId) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/space/" + spaceId,
            {},
            {},
            this.getPrivateSpaceHandler);
    }

    getSpaceBalanceForRange(startDate, endDate) {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/balance",
            {
                "start_date": startDate,
                "end_date": endDate
            },
            {},
            this.getSpaceBalanceHandler
        );
    }

    onSpaceNotFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PUBLIC_SPACE_NOT_FOUND, {});
    };

    onPrivateSpaceDetailsNotFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PRIVATE_SPACE_DETAILS_NOT_FOUND, {});
    };

    onSpaceFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.PUBLIC_SPACE_LOADED,
            new Space(
                parseInt(responseBodyContents["space_id"], 10),
                responseBodyContents["title"],
                responseBodyContents["description"],
                parseFloat(responseBodyContents["price_per_day"]),
                parseInt(responseBodyContents["minimum_days"],10),
                parseInt(responseBodyContents["status_id"], 10),
                responseBodyContents["status"],
                parseInt(responseBodyContents["member_id"], 10),
                parseInt(responseBodyContents["current_address_id"], 10),
                responseBodyContents["current_google_place_id"],
                responseBodyContents["current_latitude"],
                responseBodyContents["current_longitude"],
                responseBodyContents["current_formatted_address"],
                responseBodyContents["distance"],
                responseBodyContents["created"],
                responseBodyContents["updated"],
                responseBodyContents["deleted"],
                responseBodyContents["image_names"]

            ));
    };

    onPrivateSpaceDetailsFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.PRIVATE_SPACE_DETAILS_LOADED,
            new Space(
                parseInt(responseBodyContents["space_id"],10),
                responseBodyContents["title"],
                responseBodyContents["description"],
                parseFloat(responseBodyContents["price_per_day"]),
                parseInt(responseBodyContents["minimum_days"], 10),
                parseInt(responseBodyContents["status_id"],10),
                responseBodyContents["status"],
                parseInt(responseBodyContents["member_id"], 10),
                parseInt(responseBodyContents["current_address_id"], 10),
                responseBodyContents["current_google_place_id"],
                responseBodyContents["current_latitude"],
                responseBodyContents["current_longitude"],
                responseBodyContents["current_formatted_address"],
                responseBodyContents["distance"],
                responseBodyContents["created"],
                responseBodyContents["updated"],
                responseBodyContents["deleted"],
                responseBodyContents["image_names"]

            ));
    };

    onPrivateSpaceNotFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(ActionResultEvents.PRIVATE_SPACE_NOT_FOUND, "PRIVATE_SPACE_NOT_FOUND");
    };

    onPrivateSpaceFound = (responseHeaders, responseBodyContents, dataFromAction) => {
        const space = responseBodyContents["space"];
        EventPublisher.publish(
            ActionResultEvents.PRIVATE_SPACE_LOADED,
            new GetPrivateSpaceResponse(
                new Space(
                    parseInt(space["space_id"], 10),
                    space["title"],
                    space["description"],
                    parseFloat(space["price_per_day"]),
                    parseInt(space["minimum_days"], 10),
                    parseInt(space["status_id"], 10),
                    space["status"],
                    parseInt(space["member_id"], 10),
                    parseInt(space["current_address_id"], 10),
                    space["current_google_place_id"],
                    space["current_latitude"],
                    space["current_longitude"],
                    space["current_formatted_address"],
                    space["distance"],
                    space["created"],
                    space["updated"],
                    space["deleted"],
                    space["image_names"]),
                responseBodyContents["is_limited_edit"]));
    };

    getListedSpaces() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/spaces/listed",
            {},
            {},
            this.getListedSpacesHandler);
    }

    getUnlistedSpaces() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/spaces/unlisted",
            {},
            {},
            this.getUnlistedSpacesHandler);
    }

    getMySpaces() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/my_spaces",
            {},
            {},
            this.getMySpacesHandler);
    }

    createSpace(data) {
        SecureRequestExecuter.executePOSTRequest(
            "/api/private/space",
            {},
            {
                "title": data.title,
                "description": data.description,
                "price_per_day": data.price,
                "minimum_days": data.minimumDays,
                "google_place_id": data.spaceAddressGooglePlaceId,
                "latitude": data.spaceAddressLatitude,
                "longitude": data.spaceAddressLongitude,
                "formatted_address": data.spaceAddress,
                "image_names": data.imageNames,
            },
            {},
            this.createSpaceHandler
        );
    }

    updateSpace(data) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/space",
            {},
            {
                "title": data.title,
                "description": data.description,
                "price_per_day": data.price,
                "minimum_days": data.minimumDays,
                "google_place_id": data.spaceAddressGooglePlaceId,
                "latitude": data.spaceAddressLatitude,
                "longitude": data.spaceAddressLongitude,
                "formatted_address": data.spaceAddress,
                "space_id": data.spaceId,
                "image_names": data.imageNames,
            },
            {},
            this.updateSpaceHandler
        );
    }

    unlistSpace(data) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/space/unlist",
            {},
            {
                "space_id" : data.spaceId,
            },
            {},
            this.unlistSpaceHandler
        );
    }

    listSpace(data) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/space/list",
            {},
            {
                "space_id" : data.spaceId,
            },
            {},
            this.listSpaceHandler
        );
    }

    rejectSpaceReservation(data) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/reservations/reject",
            {},
            {
                "reservation_id" : data.reservationRequestId,
            },
            {},
            this.rejectSpaceReservationHandler
        );
    }

    acceptSpaceReservation(data) {
        SecureRequestExecuter.executePUTRequest(
            "/api/private/reservations/accept",
            {},
            {
                "reservation_id" : data.reservationRequestId,
            },
            {},
            this.acceptSpaceReservationHandler
        );
    }

    getMemberSpacesReservations() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/member/spaces/reservations",
            {},
            {},
            this.getMemberSpacesReservationsHandler
        );
    }


    getMemberAdvertisementReservations() {
        SecureRequestExecuter.executeGETRequest(
            "/api/private/member/advertisement/reservations",
            {},
            {},
            this.getMemberAdvertisementReservationsHandler
        );
    }

    onGetListedSpacesSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.LISTED_SPACES_LOADED,
            new GetSpacesResponse(
                responseBodyContents["results"].map(
                    space => new Space (
                        parseInt(space["space_id"], 10),
                        space["title"],
                        space["description"],
                        parseFloat(space["price_per_day"]),
                        parseInt(space["minimum_days"], 10),
                        parseInt(space["status_id"], 10),
                        space["status"],
                        parseInt(space["member_id"], 10),
                        parseInt(space["current_address_id"], 10),
                        space["current_google_place_id"],
                        space["current_latitude"],
                        space["current_longitude"],
                        space["current_formatted_address"],
                        space["distance"],
                        space["created"],
                        space["updated"],
                        space["deleted"],
                        space["image_names"]
                    )))
        );
    };

    onGetUnlistedSpacesSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.UNLISTED_SPACES_LOADED,
            new GetSpacesResponse(
                responseBodyContents["results"].map(
                    space => new Space (
                        parseInt(space["space_id"], 10),
                        space["title"],
                        space["description"],
                        parseFloat(space["price_per_day"]),
                        parseInt(space["minimum_days"], 10),
                        parseInt(space["status_id"], 10),
                        space["status"],
                        parseInt(space["member_id"], 10),
                        parseInt(space["current_address_id"], 10),
                        space["current_google_place_id"],
                        space["current_latitude"],
                        space["current_longitude"],
                        space["current_formatted_address"],
                        space["distance"],
                        space["created"],
                        space["updated"],
                        space["deleted"],
                        space["image_names"]
                    )))
        );
    };

    onGetMySpacesSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.MY_SPACES_LOADED,
            new GetMySpacesResponse(
                responseBodyContents["listed"].map(
                    space => new Space(
                        parseInt(space["space_id"], 10),
                        space["title"],
                        space["description"],
                        parseFloat(space["price_per_day"]),
                        parseInt(space["minimum_days"], 10),
                        parseInt(space["status_id"], 10),
                        space["status"],
                        parseInt(space["member_id"], 10),
                        parseInt(space["current_address_id"], 10),
                        space["current_google_place_id"],
                        space["current_latitude"],
                        space["current_longitude"],
                        space["current_formatted_address"],
                        space["distance"],
                        space["created"],
                        space["updated"],
                        space["deleted"],
                        space["image_names"]
                    )),
            responseBodyContents["unlisted"].map(
                space => new Space (
                    parseInt(space["space_id"], 10),
                    space["title"],
                    space["description"],
                    parseFloat(space["price_per_day"]),
                    parseInt(space["minimum_days"], 10),
                    parseInt(space["status_id"], 10),
                    space["status"],
                    parseInt(space["member_id"], 10),
                    parseInt(space["current_address_id"], 10),
                    space["current_google_place_id"],
                    space["current_latitude"],
                    space["current_longitude"],
                    space["current_formatted_address"],
                    space["distance"],
                    space["created"],
                    space["updated"],
                    space["deleted"],
                    space["image_names"]
            )))
        );
    };

    onSpaceCreatedSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.SPACE_CREATED, {});
    };

    onSpaceUpdateSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.SPACE_EDITED, {});
    };

    onSpaceRejected = (responseHeaders, responseBodyContents, dataFromAction) => {
        this.getMemberSpacesReservations();
    };

    onSpaceAccepted = (responseHeaders, responseBodyContents, dataFromAction) => {
        this.getMemberSpacesReservations();
    };

    onSpaceStatusChangeSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        this.getMySpaces();
    };

    onMemberSpacesReservationsSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.MEMBER_SPACES_RESERVATIONS_LOADED,
                new MemberSpacesReservationResponse(
                    responseBodyContents["reservation_requests"].map(reservation => new Reservation (
                        reservation["reservation_request_id"],
                        reservation["advertisement_id"],
                        reservation["advertisement_title"],
                        reservation["space_id"],
                        reservation["space_title"],
                        reservation["status_id"],
                        reservation["status_name"],
                        reservation["start_date"],
                        reservation["end_date"],
                        reservation["created"],
                        reservation["updated"],
                        reservation["deleted"],
                        reservation["price_per_day"],
                        reservation["has_over_lapping_requests"])),
                    responseBodyContents["active_reservations"].map(reservation => new Reservation(
                        reservation["reservation_request_id"],
                        reservation["advertisement_id"],
                        reservation["advertisement_title"],
                        reservation["space_id"],
                        reservation["space_title"],
                        reservation["status_id"],
                        reservation["status_name"],
                        reservation["start_date"],
                        reservation["end_date"],
                        reservation["created"],
                        reservation["updated"],
                        reservation["deleted"],
                        reservation["price_per_day"],
                        reservation["has_over_lapping_requests"])),
                    responseBodyContents["history"].map(reservation => new Reservation (
                        reservation["reservation_request_id"],
                        reservation["advertisement_id"],
                        reservation["advertisement_title"],
                        reservation["space_id"],
                        reservation["space_title"],
                        reservation["status_id"],
                        reservation["status_name"],
                        reservation["start_date"],
                        reservation["end_date"],
                        reservation["created"],
                        reservation["updated"],
                        reservation["deleted"],
                        reservation["price_per_day"],
                        reservation["has_over_lapping_requests"]))
                )
            );
    };

    onMemberAdvertisementReservationsSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        EventPublisher.publish(
            ActionResultEvents.MEMBER_ADVERTISEMENT_RESERVATIONS_LOADED,
            new MemberSpacesReservationResponse(
                responseBodyContents["reservation_requests"].map(reservation => new Reservation (
                    reservation["reservation_request_id"],
                    reservation["advertisement_id"],
                    reservation["advertisement_title"],
                    reservation["space_id"],
                    reservation["space_title"],
                    reservation["status_id"],
                    reservation["status_name"],
                    reservation["start_date"],
                    reservation["end_date"],
                    reservation["created"],
                    reservation["updated"],
                    reservation["deleted"],
                    reservation["price_per_day"],
                    reservation["has_over_lapping_requests"])),
                responseBodyContents["active_reservations"].map(reservation => new Reservation(
                    reservation["reservation_request_id"],
                    reservation["advertisement_id"],
                    reservation["advertisement_title"],
                    reservation["space_id"],
                    reservation["space_title"],
                    reservation["status_id"],
                    reservation["status_name"],
                    reservation["start_date"],
                    reservation["end_date"],
                    reservation["created"],
                    reservation["updated"],
                    reservation["deleted"],
                    reservation["price_per_day"],
                    reservation["has_over_lapping_requests"])),
                responseBodyContents["history"].map(reservation => new Reservation (
                    reservation["reservation_request_id"],
                    reservation["advertisement_id"],
                    reservation["advertisement_title"],
                    reservation["space_id"],
                    reservation["space_title"],
                    reservation["status_id"],
                    reservation["status_name"],
                    reservation["start_date"],
                    reservation["end_date"],
                    reservation["created"],
                    reservation["updated"],
                    reservation["deleted"],
                    reservation["price_per_day"],
                    reservation["has_over_lapping_requests"]))
            )
        );
    };


    onGetSpaceBalanceSuccess = (responseHeaders, responseBodyContents, dataFromAction) => {
        const earningsEvents = [];
        for (const earningsEvent of responseBodyContents["balance_events"]["earnings"]) {
            earningsEvents.push(
                new SpaceMonthEarningEvent(
                    earningsEvent["day"],
                    earningsEvent["advertisement_id"],
                    earningsEvent["advertisement_title"],
                    earningsEvent["space_id"],
                    earningsEvent["space_title"],
                    earningsEvent["price_per_day"]
                )
            );
        }

        const payoutEvents = [];
        for (const payoutEvent of responseBodyContents["balance_events"]["payouts"]) {
            payoutEvents.push(
                new SpaceMonthPayoutEvent(
                    payoutEvent["day"],
                    payoutEvent["paymentIdentifier"],
                    payoutEvent["amount"]
                )
            );
        }

        const spaceMonthBalanceEvents =
            new SpaceMonthBalanceEvents(
                responseBodyContents["prior_earnings"],
                responseBodyContents["prior_payouts"],
                earningsEvents,
                payoutEvents
            );

        EventPublisher.publish(
            ActionResultEvents.SPACE_BALANCE_LOADED,
            spaceMonthBalanceEvents
        );
    };
}